import { css } from '@/features/shared/styles'
import { keyframes } from '@/features/shared/styles/effects/keyframes'

// shipt/require-keyframes-interpolation requires every @keyframes rule below to
// be named by a `keyframes.*` interpolation, and each name to be used only once.
export const keyframesGlobalStyle = css`
  @keyframes ${keyframes.enter} {
    from {
      opacity: var(--enter-opacity, 0);
      transform: translate(var(--enter-x, 0), var(--enter-y, 0)) scale(var(--enter-scale, 1));
    }
    to {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
  }

  @keyframes ${keyframes.fade} {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes ${keyframes.fadeTo} {
    to {
      opacity: var(--fade-to, 1);
    }
  }

  @keyframes ${keyframes.translateTo} {
    to {
      transform: translate(var(--to-x, 0), var(--to-y, 0)) scale(var(--to-scale, 1));
    }
  }

  @keyframes ${keyframes.scaleToHide} {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(0.5);
      opacity: 0.5;
    }
    100% {
      transform: scale(0);
      opacity: 0;
    }
  }

  @keyframes ${keyframes.scaleToShowAndHide} {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    40% {
      transform: scale(1.5);
      opacity: 1;
    }
    80% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(0);
      opacity: 0;
    }
  }

  @keyframes ${keyframes.scaleIcon} {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.5);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* --ping-color must be an opaque color: consumers also use it directly as a
     solid background-color, so alpha is animated separately via opacity. */
  @keyframes ${keyframes.ping} {
    0% {
      box-shadow: 0 0 0 0 var(--ping-color);
      opacity: 0.5;
    }
    50%,
    100% {
      box-shadow: 0 0 0 0.5rem var(--ping-color);
      opacity: 0;
    }
  }

  @keyframes ${keyframes.targetC360ToRestingState} {
    to {
      transform: translateY(0);
    }
  }

  @keyframes ${keyframes.targetC360BagExit} {
    to {
      transform: translateX(calc(-60vw - 100%));
    }
  }

  @keyframes ${keyframes.targetC360RevealBanner} {
    to {
      transform: translateY(0);
      opacity: 1;
      pointer-events: auto;
    }
  }

  @keyframes ${keyframes.targetC360HeroHeight} {
    to {
      grid-template-rows:
        [hero-start] min(var(--hero-height), 100svh)
        [hero-end];
    }
  }

  @keyframes ${keyframes.homepageHeroHeight} {
    to {
      min-height: var(--hero-height);
    }
  }

  /* --bag-mid-x holds the start offset through the midpoint on mobile only. */
  @keyframes ${keyframes.homepageBagSequence} {
    0% {
      opacity: 0;
      transform: translate(var(--bag-from-x), 0) scale(var(--bag-from-scale));
    }
    10% {
      opacity: 1;
      transform: translate(var(--bag-from-x), 0) scale(var(--bag-from-scale));
    }
    50% {
      opacity: 1;
      transform: translate(var(--bag-mid-x, 0), 0) scale(var(--bag-from-scale));
    }
    100% {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
  }

  @keyframes ${keyframes.homepageSlideInHeadline} {
    0% {
      opacity: 0;
      bottom: var(--headline-from-bottom, 0);
      right: var(--headline-from-right, 0);
    }
    100% {
      opacity: 1;
      bottom: 0;
      right: 0;
    }
  }

  @keyframes ${keyframes.messageRoll} {
    0%,
    68% {
      transform: translateY(0);
    }
    82%,
    100% {
      transform: translateY(-100%);
    }
  }

  @keyframes ${keyframes.carouselBounce} {
    0% {
      transform: translateX(0);
    }
    47% {
      transform: translateX(-80px);
    }
    79% {
      transform: translateX(3px);
    }
    89% {
      transform: translateX(-1px);
    }
    100% {
      transform: translateX(0);
    }
  }

  @keyframes ${keyframes.expand} {
    0% {
      transform: scaleX(0) translateX(-20px) translateZ(0);
    }
    100% {
      transform: scaleX(1) translateX(0px) translateZ(0);
    }
  }

  @keyframes ${keyframes.collapse} {
    0% {
      transform: scaleX(1) translateX(0px) translateZ(0);
    }
    100% {
      transform: scaleX(0) translateX(-20px) translateZ(0);
    }
  }

  @keyframes ${keyframes.slideUp} {
    0% {
      transform: translateY(100%);
      border-radius: 1.125rem;
    }
    50% {
      border-radius: 1.125rem 1.125rem 0 0;
    }
    100% {
      transform: translateY(0);
      border-radius: 1.125rem 1.125rem 0 0;
    }
  }

  @keyframes ${keyframes.shadowFill} {
    0% {
      box-shadow: none;
    }
    100% {
      box-shadow: var(--shadow-fill);
    }
  }
`
